import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { usePathname } from 'next/navigation';
import { twMerge } from 'tailwind-merge';
import ListItem from '@/common/ListItem';
import {
  faBookSparkles,
  faBrain,
  faChartSimple,
  faGamepadModern,
  faSearch,
  faRectangleHistory,
  faUser,
  faWallet,
} from '@fortawesome/pro-thin-svg-icons';
import Text from '@/components/Text';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import useCurrentUser from '@/hooks/useCurrentUser';
import { useAuthUser } from '@/hooks/useAuthUser';
import { firebaseAuth } from '@/lib/Firebase';
import { useCallback, useEffect, useMemo } from 'react';
import InstitutionLogo from '../InstitutionLogo';
import Loader from '@/components/Loader';

function MainMenu({ className = '' }: { className?: string }) {
  const { data: authUser, isLoading: isAuthLoading } = useAuthUser(['authUser'], firebaseAuth);
  const { data: user } = useCurrentUser(!!authUser);

  const menuNavItems = useMemo(() => {
    // Unauthenticated users: show only lead magnet features + login
    if (!authUser) {
      return [
        {
          label: 'Home',
          link: '/',
          icon: <FontAwesomeIcon icon={faChartSimple as IconProp} className="size-6" />,
          enabled: true
        },
        {
          label: 'Tournaments',
          link: '/tournaments',
          icon: <FontAwesomeIcon icon={faGamepadModern as IconProp} className="size-6" />,
          enabled: true
        },
        {
          label: 'Learn',
          link: '/learn',
          icon: <FontAwesomeIcon icon={faBookSparkles as IconProp} className="size-6" />,
          enabled: true
        },
        {
          label: 'Your type',
          link: '/your-type',
          icon: <FontAwesomeIcon icon={faBrain as IconProp} className="size-6" />,
          enabled: true
        },
        {
          label: 'Log in',
          link: '/login',
          icon: <FontAwesomeIcon icon={faUser as IconProp} className="size-6" />,
          enabled: true,
          className: 'mt-auto'
        },
      ];
    }

    return [
      {
        label: 'Investing',
        link: '/',
        icon: <FontAwesomeIcon icon={faChartSimple as IconProp} className="size-6" />,
        enabled: false
      },
      {
        label: 'Tournaments',
        link: '/tournaments',
        icon: <FontAwesomeIcon icon={faGamepadModern as IconProp} className="size-6" />,
        enabled: true
      },
      {
        label: 'Learn',
        link: '/learn',
        icon: <FontAwesomeIcon icon={faBookSparkles as IconProp} className="size-6" />,
        enabled: true
      },
      {
        label: 'Your type',
        link: '/your-type',
        icon: <FontAwesomeIcon icon={faBrain as IconProp} className="size-6" />,
        enabled: true
      },
      {
        label: 'Discover',
        link: '/discover',
        icon: <FontAwesomeIcon icon={faSearch as IconProp} className="size-6" />,
        enabled: true
      },
      {
        label: 'Wallet',
        link: '/wallet',
        icon: <FontAwesomeIcon icon={faWallet as IconProp} className="size-6" />,
        enabled: true
      },
      {
        label: 'Activity',
        link: '/activity',
        icon: <FontAwesomeIcon icon={faRectangleHistory as IconProp} className="size-6" />,
        enabled: true
      },
      {
        label: 'Profile',
        link: '/profile',
        icon: <FontAwesomeIcon icon={faUser as IconProp} className="size-6" />,
        enabled: true
      },
    ];
  }, [authUser, user]);

  const pathname = usePathname();
  const isActive = (href: string, exact: boolean = true) => {
    if (exact) {
      return pathname === href;
    }
    return pathname?.startsWith(href);
  };

  const renderItem = useCallback((item: any, index: number) => {
    return (
      <ListItem
        key={`${item?.label}-${index}`}
        icon={item?.icon}
        className={twMerge(
          'lg:w-full lg:flex-1 justify-center flex-1 md:items-center xl:justify-start px-6 gap-4 rounded-xl cursor-pointer lg:divider-none divider',
          isActive(item?.link ?? '*') && 'bg-darkglass [&_>svg]:text-teal-200',
          !item?.link && 'pointer-events-none',
          item?.className
        )}
        href={item?.link ?? ''}
      >
        <ListItem.Left className="md:hidden xl:flex">
          <Text className='text-lg lg:text-md'>{item?.label}</Text>
        </ListItem.Left>
      </ListItem>
    );
  }, [pathname]);

  if (isAuthLoading && !authUser) return <Loader className='size-10 mx-auto my-8' />;

  return (
      <div className={twMerge('md:gap-0 h-full flex flex-col gap-4', className)}>
        <div className='flex-grow h-full w-full overflow-y-auto scrollbar-apple lg:scrollbar-apple__stable-auto'>
          {menuNavItems.map((item, index, items) => {
            if (!item.enabled) return null;
            return renderItem(item, index);
          })}
        </div>
        {authUser && (
          <div className='relative flex-grow flex flex-col place-self-center mx-auto lg:order-1 scale-100 md:scale-75 xl:scale-100 xl:flex my-2 h-[120px]'>
            <InstitutionLogo width={64} showName={true} />
          </div>
        )}
      </div>
  );
}

export default MainMenu;
